import { loadSystems } from "../../data/loadSystems";
import { MARKET_ITEMS, type MarketCategory } from "../../data/marketItems";
import { volumePerUnitM3 } from "../../resourceCatalog";
import {
  canSellHere,
  currentMarketEpoch,
  msUntilNextEpoch,
  stationBuyPrice,
  stationSellPrice,
  stationStocks,
  type GalaxyRegion,
  type StationClass
} from "../../market/stationMarket";
import { state } from "../state";
import { triggerSave } from "../persistence";
import type { ScreenContext } from "../app";
import { renderHeader } from "../components";

/**
 * Galaxy Prices (design doc 16.93, Don's direction): where in the galaxy each
 * good is dearest and cheapest right now, and a click to set course.
 *
 * The Market screen answers "what should I do here". This one answers "where
 * should I go", which is the question a hauler actually spends their time on
 * and which no screen could answer before -- prices were only visible by flying
 * to a station and looking.
 *
 * A note on naming, because the two screens read the numbers from opposite
 * sides. On the Market screen "Buy Price" is what YOU pay; here "Low Price" is
 * the cheapest place to acquire something and "High Price" is where you are
 * paid most for it. Both column headers say which side they mean, since a table
 * of bare "high" and "low" invites exactly the wrong trade.
 */

type SortKey = "item" | "type" | "high" | "low" | "margin";

interface OverviewRow {
  name: string;
  category: MarketCategory;
  volumePerUnit: number;
  /** Most any station will pay you, and where. Null when nowhere may buy it. */
  highPrice: number | null;
  highAt: { id: string; name: string } | null;
  /** Least any station charges you, and where. Null when nobody stocks it. */
  lowPrice: number | null;
  lowAt: { id: string; name: string } | null;
  /** Margin per m3 on the best round trip, or null if either end is missing. */
  marginPerM3: number | null;
}

let sortKey: SortKey = "margin";
let sortDesc = true;

export function renderMarketOverviewScreen(container: HTMLElement, ctx: ScreenContext): void {
  const wrap = document.createElement("div");
  const epoch = currentMarketEpoch(Date.now());

  const stations = loadSystems()
    .filter((s) => s.hasStation)
    .map((s) => ({
      id: s.id,
      name: s.name,
      region: s.region as GalaxyRegion,
      cls: (s.stationClass ?? "hub") as StationClass
    }));

  function buildRows(): OverviewRow[] {
    return MARKET_ITEMS.map((item) => {
      let highPrice: number | null = null;
      let highAt: { id: string; name: string } | null = null;
      let lowPrice: number | null = null;
      let lowAt: { id: string; name: string } | null = null;

      stations.forEach((station) => {
        // Where you can SELL it. Gated by canSellHere, so Contraband correctly
        // reports only pirate stations rather than the best price in the
        // galaxy at a station that would refuse the cargo.
        if (canSellHere(item.name, station.region)) {
          const paid = stationSellPrice(station.id, station.region, item.name, epoch);
          if (highPrice === null || paid > highPrice) {
            highPrice = paid;
            highAt = { id: station.id, name: station.name };
          }
        }
        // Where you can BUY it -- only stations that actually stock it. A
        // station's notional price for something it does not carry is not a
        // place you can go and buy it, and listing it would send a hauler on a
        // wasted trip.
        if (stationStocks(station.id, item.name, epoch)) {
          const charged = stationBuyPrice(station.id, station.region, item.name, epoch);
          if (lowPrice === null || charged < lowPrice) {
            lowPrice = charged;
            lowAt = { id: station.id, name: station.name };
          }
        }
      });

      const per = volumePerUnitM3(item.name) || 1;
      return {
        name: item.name,
        category: item.category,
        volumePerUnit: per,
        highPrice,
        highAt,
        lowPrice,
        lowAt,
        marginPerM3: highPrice !== null && lowPrice !== null ? (highPrice - lowPrice) / per : null
      };
    });
  }

  function sortRows(rows: OverviewRow[]): OverviewRow[] {
    const dir = sortDesc ? -1 : 1;
    const value = (r: OverviewRow): number | string => {
      switch (sortKey) {
        case "type":
          return r.category;
        case "high":
          return r.highPrice ?? -Infinity;
        case "low":
          return r.lowPrice ?? Infinity;
        case "margin":
          return r.marginPerM3 ?? -Infinity;
        default:
          return r.name;
      }
    };
    return [...rows].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (typeof av === "string" || typeof bv === "string") {
        const cmp = String(av).localeCompare(String(bv));
        return cmp !== 0 ? cmp * dir : a.name.localeCompare(b.name);
      }
      if (av === bv) return a.name.localeCompare(b.name);
      return (av - bv) * dir;
    });
  }

  function setWaypoint(systemId: string, systemName: string): void {
    // Toggling off is deliberate: clicking the station you already have a
    // course to is far more likely to mean "cancel that" than "set it again".
    if (state.waypointSystemId === systemId) {
      state.waypointSystemId = null;
      state.lastEvent = `Course to ${systemName} cleared.`;
    } else {
      state.waypointSystemId = systemId;
      state.lastEvent = `Course set for ${systemName}.`;
    }
    triggerSave(ctx);
    render();
  }

  function render(): void {
    wrap.innerHTML = "";
    renderHeader(wrap, ctx);

    const rows = sortRows(buildRows());
    const minutesLeft = Math.ceil(msUntilNextEpoch(Date.now()) / 60000);
    const waypointName = state.waypointSystemId
      ? (stations.find((s) => s.id === state.waypointSystemId)?.name ?? state.waypointSystemId)
      : null;

    const intro = document.createElement("div");
    intro.innerHTML = `
      <h1>Galaxy Prices</h1>
      <p class="dim">
        ${stations.length} stations &middot; prices move in ${minutesLeft} min &middot;
        click a station to set course${waypointName ? ` &middot; currently: <strong>${escapeHtml(waypointName)}</strong>` : ""}
      </p>
      <p class="dim">
        <strong>High</strong> is the most anyone will pay you. <strong>Low</strong> is the
        least anyone will charge you, counting only stations that actually stock it.
        Contraband shows pirate stations only &mdash; nowhere else will take it.
      </p>
    `;
    wrap.appendChild(intro);

    if (state.lastEvent) {
      const log = document.createElement("p");
      log.className = "mk-log";
      log.textContent = state.lastEvent;
      wrap.appendChild(log);
    }

    const scroll = document.createElement("div");
    scroll.className = "mk-scroll";
    scroll.innerHTML = `
      <table class="mk-table">
        <thead>
          <tr>
            ${header("item", "Item")}
            ${header("type", "Type")}
            ${header("high", "High — sell to")}
            <th>Location</th>
            ${header("low", "Low — buy at")}
            <th>Location</th>
            ${header("margin", "Margin CR/m³")}
          </tr>
        </thead>
        <tbody>${rows.map(rowHtml).join("")}</tbody>
      </table>
    `;
    wrap.appendChild(scroll);
    wire(scroll);
  }

  function header(key: SortKey, label: string): string {
    const active = sortKey === key;
    const arrow = active ? (sortDesc ? " ▾" : " ▴") : "";
    return `<th><button type="button" class="mk-sort ${active ? "active" : ""}" data-sort="${key}">${label}${arrow}</button></th>`;
  }

  function stationCell(at: { id: string; name: string } | null): string {
    if (!at) return `<td class="dim">—</td>`;
    const current = state.waypointSystemId === at.id;
    return `<td><button type="button" class="mk-place ${current ? "active" : ""}" data-goto="${at.id}" data-goto-name="${escapeHtml(at.name)}">${escapeHtml(at.name)}${current ? " ✓" : ""}</button></td>`;
  }

  function rowHtml(r: OverviewRow): string {
    return `
      <tr>
        <td>${escapeHtml(r.name)}</td>
        <td class="dim">${escapeHtml(r.category)}</td>
        <td class="mk-num">${r.highPrice === null ? "—" : r.highPrice.toFixed(2)}</td>
        ${stationCell(r.highAt)}
        <td class="mk-num">${r.lowPrice === null ? "—" : r.lowPrice.toFixed(2)}</td>
        ${stationCell(r.lowAt)}
        <td class="mk-num ${r.marginPerM3 !== null && r.marginPerM3 > 0 ? "mk-good" : "dim"}">
          ${r.marginPerM3 === null ? "—" : r.marginPerM3.toFixed(2)}
        </td>
      </tr>
    `;
  }

  function wire(scope: HTMLElement): void {
    scope.querySelectorAll<HTMLButtonElement>("[data-sort]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const key = btn.dataset.sort as SortKey;
        if (sortKey === key) sortDesc = !sortDesc;
        else {
          sortKey = key;
          // "Low" wants cheapest first; every other numeric column wants
          // best-first, and the text columns read naturally ascending.
          sortDesc = key !== "item" && key !== "type" && key !== "low";
        }
        render();
      });
    });
    scope.querySelectorAll<HTMLButtonElement>("[data-goto]").forEach((btn) => {
      btn.addEventListener("click", () => setWaypoint(btn.dataset.goto!, btn.dataset.gotoName!));
    });
  }

  render();
  container.appendChild(wrap);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
